import type { WorkJourneyFiltersState } from '@src/packages/workJourney/types';
import { normalizeDaysOfWeek } from '@src/packages/utils/dates/formatDaysOfWeek';
import { WORK_JOURNEY_STATUS } from '@src/packages/workJourney/constants';
import { WORK_JOURNEY_FILTER_DEFAULTS } from '@src/packages/workJourney/workJourneyListFilterConfig';

export const hasTextWorkJourneyFilters = ({
  search,
  userIds,
}: Pick<WorkJourneyFiltersState, 'search' | 'userIds'>): boolean =>
  Boolean(search.trim()) || userIds.length > 0;

export const hasFirestoreWorkJourneyFilters = ({
  shift,
  state,
  dateRange,
}: Pick<WorkJourneyFiltersState, 'shift' | 'state' | 'dateRange'>): boolean =>
  shift !== WORK_JOURNEY_FILTER_DEFAULTS.SHIFT ||
  state !== WORK_JOURNEY_FILTER_DEFAULTS.STATE ||
  dateRange.length > 0;

export const hasAnyWorkJourneyListFilters = (filters: WorkJourneyFiltersState): boolean =>
  hasTextWorkJourneyFilters(filters) || hasFirestoreWorkJourneyFilters(filters);

export const hasPersistedBootstrapWorkJourneyFilters = (
  filters: WorkJourneyFiltersState,
): boolean =>
  hasTextWorkJourneyFilters(filters) ||
  filters.shift !== WORK_JOURNEY_FILTER_DEFAULTS.SHIFT ||
  filters.dateRange.length > 0 ||
  (filters.state !== WORK_JOURNEY_FILTER_DEFAULTS.STATE &&
    filters.state !== WORK_JOURNEY_STATUS.ACTIVE);

const normalizeWorkJourneyDateRange = (dateRange: WorkJourneyFiltersState['dateRange']): number[] =>
  normalizeDaysOfWeek(dateRange);

export const areWorkJourneyListFiltersEqual = (
  left: WorkJourneyFiltersState,
  right: WorkJourneyFiltersState,
): boolean => {
  const leftDays = normalizeWorkJourneyDateRange(left.dateRange);
  const rightDays = normalizeWorkJourneyDateRange(right.dateRange);

  return (
    left.search.trim() === right.search.trim() &&
    left.userIds.length === right.userIds.length &&
    left.userIds.every((userId, index) => userId === right.userIds[index]) &&
    left.shift === right.shift &&
    left.state === right.state &&
    leftDays.length === rightDays.length &&
    leftDays.every((day, index) => day === rightDays[index])
  );
};

export const shouldUsePersistedListFiltersBootstrap = (
  debouncedFilters: WorkJourneyFiltersState,
  persistedFilters: WorkJourneyFiltersState,
  bootstrapComplete: boolean,
): boolean =>
  !bootstrapComplete &&
  hasPersistedBootstrapWorkJourneyFilters(persistedFilters) &&
  !areWorkJourneyListFiltersEqual(debouncedFilters, persistedFilters);

export const shouldCompletePersistedListFiltersBootstrap = (
  debouncedFilters: WorkJourneyFiltersState,
  persistedFilters: WorkJourneyFiltersState,
  currentFilters: WorkJourneyFiltersState,
): boolean => {
  if (areWorkJourneyListFiltersEqual(debouncedFilters, persistedFilters)) {
    return true;
  }

  if (!hasPersistedBootstrapWorkJourneyFilters(currentFilters)) {
    // Remount hydration: panel values are still empty while stored filters exist.
    if (
      !hasPersistedBootstrapWorkJourneyFilters(debouncedFilters) &&
      hasPersistedBootstrapWorkJourneyFilters(persistedFilters)
    ) {
      return false;
    }

    // User cleared active filters — stop bootstrapping from the mount snapshot.
    return true;
  }

  return false;
};

export const intersectWorkJourneyConfigIds = (left: string[], right: string[]): string[] => {
  if (!left.length || !right.length) {
    return [];
  }

  const rightIds = new Set(right);
  return left.filter(id => rightIds.has(id));
};
